import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { joinGame, addNpcToLobby, removeNpcFromLobby, clearNpcs, requestSeatNextRound, respondWaitlistOffer, leaveGame, startGame, getGame, getAllTimeLeaderboard, getPeriodLeaderboard, updateGamePersistence, updateGameSettings, collapseInactiveAndRedeal, proposeCollapseInactiveRedeal, bumpPlayerToWaitlist, getChatMessagesByTarget, deleteChatMessage } from '../api/gameApi';
import { useAuth } from '../context/AuthContext';
import ThinHeader from '../components/ThinHeader';
import UniversalChatDock from '../components/UniversalChatDock';
import useRoomVoiceChat from '../hooks/useRoomVoiceChat';
import VoiceRemoteAudio from '../components/VoiceRemoteAudio';
import {
  CHAT_SCOPE,
  getUserId,
  buildRoomLoungeTarget,
  buildDmTarget,
  buildDmInboxTarget,
  toNormalizedMessage,
  messageMatchesTarget,
  buildOutgoingMessage,
  getPlaceholderForTarget,
  dedupeAndSortNormalizedMessages,
  computeUnreadByTarget,
} from '../utils/universalChat';

const inferSocketUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:5030';

  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/api\/?$/, '');
  }

  const host = window.location.hostname.toLowerCase();
  if (host === 'frolickingelfstudio.ca' || host.endsWith('.frolickingelfstudio.ca')) {
    return 'https://api-necro.frolickingelfstudio.ca';
  }

  return process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5030';
};

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || inferSocketUrl();

const toDmVoicePipeId = (currentUserId, peerUserId) => {
  const ids = [`${currentUserId || ''}`.trim(), `${peerUserId || ''}`.trim()].filter(Boolean).sort();
  if (ids.length !== 2) return 'room';
  return `dm:${ids[0]}:${ids[1]}`;
};

const toVoicePipeIdFromTarget = (target, currentUserId) => {
  if (!target) return 'room';
  if (target.type === CHAT_SCOPE.DM && target.recipientId) {
    return toDmVoicePipeId(currentUserId, target.recipientId);
  }
  return 'room';
};

const toShortPipeCode = (value) => {
  const raw = `${value || ''}`.trim();
  if (!raw) return '';
  const clean = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return clean.slice(-6) || clean;
};

const formatRoomStatus = (status) => {
  if (status === 'lobby' || status === 'finished') return 'Waiting Room';
  if (status === 'swap') return 'Game Setup (Swap)';
  if (status === 'playing') return 'Game In Play';
  return status || 'Unknown';
};

const formatJoinWindowReason = (reason) => {
  if (reason === 'lobby-seat-change') return 'A new player joined. Waitlist response window reset';
  if (reason === 'round-finished') return 'Round ended. Confirming waitlist entries for next round';
  return 'Waitlist response window active';
};

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return 'unknown';
  const ts = new Date(dateInput).getTime();
  if (!Number.isFinite(ts)) return 'unknown';

  const deltaSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
};

const formatLeaderboardActivity = (entry) => {
  if (entry?.onlineNow) {
    return { label: 'Online now', title: 'Player is online in Necronomicon right now', cssClass: 'online' };
  }
  if (entry?.lastActiveAt) {
    const exact = new Date(entry.lastActiveAt).toLocaleString();
    return {
      label: `Last active ${formatRelativeTime(entry.lastActiveAt)}`,
      title: `Last active ${exact}`,
      cssClass: 'offline',
    };
  }
  return { label: 'Last active unknown', title: 'No recent activity recorded', cssClass: 'offline' };
};

const LobbyPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canModerateChat = ['admin', 'moderator', 'stickler'].includes(user?.role);
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [showHeaderLeaderboard, setShowHeaderLeaderboard] = useState(false);
  const [leaderboardMode, setLeaderboardMode] = useState('all-time');
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [periodLeaderboard, setPeriodLeaderboard] = useState([]);
  const [periodMeta, setPeriodMeta] = useState(null);
  const [showAllTimeAll, setShowAllTimeAll] = useState(false);
  const [allTimeLoading, setAllTimeLoading] = useState(false);
  const [persistenceSaving, setPersistenceSaving] = useState(false);
  const [chatText, setChatText] = useState('');
  const [selectedChatTargetId, setSelectedChatTargetId] = useState(() => `room-game:${id}`);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [selectedTargetHistoryMessages, setSelectedTargetHistoryMessages] = useState([]);
  const [lastSeenAtByTargetId, setLastSeenAtByTargetId] = useState({});
  const [voiceEvent, setVoiceEvent] = useState(null);
  const [staleActionMessage, setStaleActionMessage] = useState('');
  const [collapsingInactive, setCollapsingInactive] = useState(false);
  const [bumpingUserId, setBumpingUserId] = useState('');
  const [roomNameInput, setRoomNameInput] = useState('');
  const [renamingRoom, setRenamingRoom] = useState(false);
  const [roomSettingsOpen, setRoomSettingsOpen] = useState(false);
  const [editingAdvancedSettings, setEditingAdvancedSettings] = useState(false);
  const [savingAdvancedSettings, setSavingAdvancedSettings] = useState(false);
  const [activeWaitlistOffer, setActiveWaitlistOffer] = useState(null);
  const [offerSecondsRemaining, setOfferSecondsRemaining] = useState(0);
  const [respondingToOffer, setRespondingToOffer] = useState(false);
  const [activeInactivityPrompt, setActiveInactivityPrompt] = useState(null);
  const [inactivityPromptSecondsRemaining, setInactivityPromptSecondsRemaining] = useState(0);
  const [respondingToInactivityPrompt, setRespondingToInactivityPrompt] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({
    tournamentEnabled: false,
    tournamentRecurrence: 'none',
    tournamentLabel: '',
    tournamentMode: 'live',
    tournamentInactivityTimerEnabled: false,
    tournamentInactivityTimeoutMinutes: 30,
  });
  const socketRef = useRef(null);
  const lastKnownStatusRef = useRef(null);
  const dismissedWaitlistOfferIdsRef = useRef(new Set());
  const waitlistDeclineBaselineRef = useRef(null);
  const forceWaitingRoomView = searchParams.get('view') === 'waiting';

  const getCurrentSeatedHumanIds = useCallback((state) => {
    const players = Array.isArray(state?.players) ? state.players : [];
    return new Set(
      players
        .filter((player) => player?.userId && !player?.isBot)
        .map((player) => `${player.userId}`)
    );
  }, []);

  const canShowNewWaitlistOffer = useCallback((state) => {
    const baseline = waitlistDeclineBaselineRef.current;
    if (!baseline) return true;

    const currentSeated = getCurrentSeatedHumanIds(state);
    const hasNewSeatedJoin = Array.from(currentSeated).some((userId) => !baseline.has(userId));
    if (hasNewSeatedJoin) {
      waitlistDeclineBaselineRef.current = null;
      return true;
    }
    return false;
  }, [getCurrentSeatedHumanIds]);

  const joinVoiceRoom = useCallback((target = 'lounge') => {
    socketRef.current?.emit('voice-join', { gameId: id, target });
  }, [id]);

  const leaveVoiceRoom = useCallback((target = 'lounge') => {
    socketRef.current?.emit('voice-leave', { gameId: id, target });
  }, [id]);

  const sendVoiceOffer = useCallback((toUserId, sdp, target = 'lounge') => {
    socketRef.current?.emit('voice-offer', { gameId: id, target, toUserId, sdp });
  }, [id]);

  const sendVoiceAnswer = useCallback((toUserId, sdp, target = 'lounge') => {
    socketRef.current?.emit('voice-answer', { gameId: id, target, toUserId, sdp });
  }, [id]);

  const sendVoiceIceCandidate = useCallback((toUserId, candidate, target = 'lounge') => {
    socketRef.current?.emit('voice-ice-candidate', {
      gameId: id,
      target,
      toUserId,
      candidate,
    });
  }, [id]);

  const sendVoiceDecline = useCallback((toUserId, reason = '', target = 'lounge') => {
    socketRef.current?.emit('voice-decline', {
      gameId: id,
      target,
      toUserId,
      reason,
    });
  }, [id]);

  const {
    isJoined: voiceJoined,
    isMuted: voiceMuted,
    voiceError,
    activeTarget: activeVoiceTarget,
    activePipeId,
    remotePeers,
    join: joinVoice,
    joinTarget: joinVoiceTarget,
    leave: leaveVoice,
    toggleMute: toggleVoiceMute,
    callPeer,
    incomingCall,
    acceptIncomingCall,
    declineIncomingCall,
  } = useRoomVoiceChat({
    voiceTarget: 'lounge',
    localUserId: user?.id,
    localDisplayName: user?.displayName || user?.username || 'Player',
    voiceEvent,
    joinVoiceRoom,
    leaveVoiceRoom,
    sendVoiceOffer,
    sendVoiceAnswer,
    sendVoiceIceCandidate,
    sendVoiceDecline,
  });

  const shouldAutoOpenPlay = useCallback((nextStatus, currentlySeated, currentlyQueued) => {
    if (!(currentlySeated || currentlyQueued)) return false;
    if (!(nextStatus === 'swap' || nextStatus === 'playing')) return false;

    // In manual waiting-room mode, still auto-open when a fresh round starts
    // from lobby/finished -> swap/playing. Keep waiting mode for ongoing active phases.
    if (!forceWaitingRoomView) return true;
    const prevStatus = lastKnownStatusRef.current;
    return prevStatus === 'lobby' || prevStatus === 'finished';
  }, [forceWaitingRoomView]);

  const fetchAllTimeLeaderboard = useCallback(async (showAll = false) => {
    try {
      setAllTimeLoading(true);
      if (leaderboardMode === 'all-time') {
        const limit = showAll ? undefined : 10;
        const { data } = await getAllTimeLeaderboard(limit);
        setAllTimeLeaderboard(data?.leaderboard || []);
        setPeriodMeta(null);
      } else {
        const { data } = await getPeriodLeaderboard({
          period: leaderboardMode,
          limit: 50,
          tournamentOnly: true,
        });
        setPeriodLeaderboard(data?.leaderboard || []);
        setPeriodMeta({
          periodLabel: data?.periodLabel || '',
          tournamentName: data?.tournamentName || 'Tournament',
          participantsTotal: Number(data?.participantsTotal || 0),
        });
      }
    } catch {
      if (leaderboardMode === 'all-time') {
        setAllTimeLeaderboard([]);
        setPeriodMeta(null);
      } else {
        setPeriodLeaderboard([]);
        setPeriodMeta(null);
      }
    } finally {
      setAllTimeLoading(false);
    }
  }, [leaderboardMode]);

  const fetchGame = useCallback(async () => {
    try {
      const { data } = await getGame(id);
      setGame(data);
      const viewerIdStr = `${user?.id || ''}`;
      const currentlySeated = typeof data?.viewerIsSeated === 'boolean'
        ? data.viewerIsSeated
        : (data.players || []).some((p) => `${p?.userId || ''}` === viewerIdStr);
      const currentlyQueued = typeof data?.viewerIsQueued === 'boolean'
        ? data.viewerIsQueued
        : (data.queuedPlayers || []).some((p) => `${p?.userId || ''}` === viewerIdStr);
      const pendingSeat = typeof data?.viewerIsPending === 'boolean'
        ? data.viewerIsPending
        : (data.pendingJoinRequests || []).some((p) => `${p?.userId || ''}` === viewerIdStr);

      const offerId = `${data?.viewerWaitlistOffer?.offerId || ''}`;
      if (
        offerId
        && !currentlySeated
        && !dismissedWaitlistOfferIdsRef.current.has(offerId)
        && canShowNewWaitlistOffer(data)
      ) {
        setActiveWaitlistOffer(data.viewerWaitlistOffer);
        setOfferSecondsRemaining(Number(data.viewerWaitlistOffer.secondsRemaining || 0));
      } else {
        setActiveWaitlistOffer(null);
        setOfferSecondsRemaining(0);
      }

      if (data?.viewerInactivityPrompt?.promptId) {
        setActiveInactivityPrompt(data.viewerInactivityPrompt);
        setInactivityPromptSecondsRemaining(Number(data.viewerInactivityPrompt.secondsRemaining || 0));
      } else {
        setActiveInactivityPrompt(null);
        setInactivityPromptSecondsRemaining(0);
      }

      if (socketRef.current && (currentlySeated || currentlyQueued || pendingSeat)) {
        socketRef.current.emit('join-room', { gameId: id });
      }

      if (shouldAutoOpenPlay(data.status, currentlySeated, currentlyQueued)) {
        navigate(`/game/${id}/play`);
      }
      lastKnownStatusRef.current = data.status;
    } catch {
      setError('Could not load game');
    }
  }, [canShowNewWaitlistOffer, id, navigate, shouldAutoOpenPlay, user?.id]);

  useEffect(() => {
    fetchGame();
    const interval = setInterval(fetchGame, 3000);
    return () => clearInterval(interval);
  }, [fetchGame]);

  useEffect(() => {
    setRoomNameInput(game?.gameName || '');
  }, [game?.gameName]);

  useEffect(() => {
    if (editingAdvancedSettings || savingAdvancedSettings) return;
    setSettingsDraft({
      tournamentEnabled: !!game?.tournament?.enabled,
      tournamentRecurrence: game?.tournament?.recurrence || 'none',
      tournamentLabel: game?.tournament?.label || '',
      tournamentMode: game?.tournament?.mode || 'live',
      tournamentInactivityTimerEnabled: !!game?.tournament?.inactivityTimerEnabled,
      tournamentInactivityTimeoutMinutes: Number(game?.tournament?.inactivityTimeoutMinutes || 30),
    });
  }, [editingAdvancedSettings, game?.tournament, savingAdvancedSettings]);

  useEffect(() => {
    if (!showHeaderLeaderboard) return;
    fetchAllTimeLeaderboard(showAllTimeAll);
  }, [showHeaderLeaderboard, showAllTimeAll, fetchAllTimeLeaderboard]);

  useEffect(() => {
    if (!id) return undefined;
    const token = localStorage.getItem('accessToken');
    if (!token) return undefined;

    const socket = io(`${SOCKET_URL}/game`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
      socket.emit('join-room', { gameId: id });
    });
    socketRef.current = socket;

    socket.on('game-state', (state) => {
      setGame(state);
      const viewerIdStr = `${user?.id || ''}`;
      const currentlySeated = typeof state?.viewerIsSeated === 'boolean'
        ? state.viewerIsSeated
        : (state.players || []).some((p) => `${p?.userId || ''}` === viewerIdStr);
      const currentlyQueued = typeof state?.viewerIsQueued === 'boolean'
        ? state.viewerIsQueued
        : (state.queuedPlayers || []).some((p) => `${p?.userId || ''}` === viewerIdStr);
      const offerId = `${state?.viewerWaitlistOffer?.offerId || ''}`;
      if (
        offerId
        && !currentlySeated
        && !dismissedWaitlistOfferIdsRef.current.has(offerId)
        && canShowNewWaitlistOffer(state)
      ) {
        setActiveWaitlistOffer(state.viewerWaitlistOffer);
        setOfferSecondsRemaining(Number(state.viewerWaitlistOffer.secondsRemaining || 0));
      } else {
        setActiveWaitlistOffer(null);
        setOfferSecondsRemaining(0);
      }
      if (state?.viewerInactivityPrompt?.promptId) {
        setActiveInactivityPrompt(state.viewerInactivityPrompt);
        setInactivityPromptSecondsRemaining(Number(state.viewerInactivityPrompt.secondsRemaining || 0));
      } else {
        setActiveInactivityPrompt(null);
        setInactivityPromptSecondsRemaining(0);
      }
      if (shouldAutoOpenPlay(state.status, currentlySeated, currentlyQueued)) {
        navigate(`/game/${id}/play`);
      }
      lastKnownStatusRef.current = state.status;
    });

    socket.on('inactivity-check-prompt', (payload = {}) => {
      const targeted = Array.isArray(payload.targetedUserIds)
        ? payload.targetedUserIds.map((uid) => `${uid}`)
        : [];
      if (targeted.length > 0 && !targeted.includes(String(user?.id || ''))) return;
      const expiresAtMs = payload.expiresAt ? new Date(payload.expiresAt).getTime() : NaN;
      const seconds = Number.isFinite(expiresAtMs)
        ? Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
        : Math.max(0, Number(payload.secondsRemaining || 0));
      setActiveInactivityPrompt(payload);
      setInactivityPromptSecondsRemaining(seconds);
    });

    socket.on('room-update', (state) => {
      setGame(state);
      lastKnownStatusRef.current = state.status;
    });

    socket.on('error', ({ message }) => {
      // Join-room can fail before user requests a seat; keep polling fallback active.
      if (message && !message.includes('You are not in this game')) {
        setError(message);
      }
    });
    socket.on('voice-room-state', (payload) => setVoiceEvent({ type: 'voice-room-state', payload: payload || {} }));
    socket.on('voice-user-joined', (payload) => setVoiceEvent({ type: 'voice-user-joined', payload: payload || {} }));
    socket.on('voice-user-left', (payload) => setVoiceEvent({ type: 'voice-user-left', payload: payload || {} }));
    socket.on('voice-offer', (payload) => setVoiceEvent({ type: 'voice-offer', payload: payload || {} }));
    socket.on('voice-answer', (payload) => setVoiceEvent({ type: 'voice-answer', payload: payload || {} }));
    socket.on('voice-ice-candidate', (payload) => setVoiceEvent({ type: 'voice-ice-candidate', payload: payload || {} }));

    return () => {
      socket.off('connect');
      socket.off('game-state');
      socket.off('room-update');
      socket.off('inactivity-check-prompt');
      socket.off('error');
      socket.off('voice-room-state');
      socket.off('voice-user-joined');
      socket.off('voice-user-left');
      socket.off('voice-offer');
      socket.off('voice-answer');
      socket.off('voice-ice-candidate');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [canShowNewWaitlistOffer, id, navigate, shouldAutoOpenPlay, user?.id]);

  useEffect(() => {
    const nextTargetId = `room-game:${id}`;
    setSelectedChatTargetId(nextTargetId);
    setLastSeenAtByTargetId((prev) => ({
      ...prev,
      [nextTargetId]: new Date().toISOString(),
    }));
  }, [id]);

  useEffect(() => {
    const targetId = `${selectedChatTargetId || ''}`;
    const parts = targetId.split(':');
    const kind = parts[0] || '';
    const value = parts.slice(1).join(':');

    const target = {
      type: kind === 'room-lounge'
        ? CHAT_SCOPE.ROOM_GAME
        : kind === 'room-game'
          ? CHAT_SCOPE.ROOM_GAME
          : kind === 'dm'
            ? CHAT_SCOPE.DM
            : kind === 'home-lounge'
              ? CHAT_SCOPE.LOUNGE
              : null,
      roomId: (kind === 'room-lounge' || kind === 'room-game') ? value : null,
      recipientId: kind === 'dm' ? value : null,
    };
    if (!target.type) return;

    let cancelled = false;
    const loadTargetHistory = async () => {
      try {
        const { data } = await getChatMessagesByTarget({
          scope: target.type,
          roomId: target.roomId || undefined,
          recipientId: target.recipientId || undefined,
          limit: 160,
        });
        if (cancelled) return;
        setSelectedTargetHistoryMessages(data?.messages || []);
      } catch {
        if (cancelled) return;
        setSelectedTargetHistoryMessages([]);
      }
    };

    loadTargetHistory();
    return () => {
      cancelled = true;
    };
  }, [selectedChatTargetId]);

  useEffect(() => {
    if (!activeWaitlistOffer?.offerId) return undefined;
    const timer = setInterval(() => {
      setOfferSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeWaitlistOffer?.offerId]);

  useEffect(() => {
    if (!activeInactivityPrompt?.promptId) return undefined;
    const timer = setInterval(() => {
      setInactivityPromptSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeInactivityPrompt?.promptId]);

  const handleWaitlistOfferResponse = useCallback(async (accept) => {
    if (!activeWaitlistOffer?.offerId || respondingToOffer) return;
    try {
      setRespondingToOffer(true);
      if (!accept) {
        dismissedWaitlistOfferIdsRef.current.add(`${activeWaitlistOffer.offerId}`);
        waitlistDeclineBaselineRef.current = getCurrentSeatedHumanIds(game);
      } else {
        waitlistDeclineBaselineRef.current = null;
      }
      await respondWaitlistOffer(id, activeWaitlistOffer.offerId, accept);
      setActiveWaitlistOffer(null);
      setOfferSecondsRemaining(0);
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not process waitlist offer');
      if (!accept) {
        setActiveWaitlistOffer(null);
        setOfferSecondsRemaining(0);
      }
    } finally {
      setRespondingToOffer(false);
    }
  }, [activeWaitlistOffer?.offerId, fetchGame, game, getCurrentSeatedHumanIds, id, respondingToOffer]);

  useEffect(() => {
    if (!activeWaitlistOffer?.offerId) return;
    if (offerSecondsRemaining > 0) return;
    handleWaitlistOfferResponse(false);
  }, [activeWaitlistOffer?.offerId, offerSecondsRemaining, handleWaitlistOfferResponse]);

  const handleInactivityPromptResponse = useCallback(async (stillPlaying) => {
    if (!activeInactivityPrompt?.promptId || !socketRef.current || respondingToInactivityPrompt) return;
    try {
      setRespondingToInactivityPrompt(true);
      await new Promise((resolve, reject) => {
        socketRef.current.emit(
          'inactivity-check-response',
          { promptId: activeInactivityPrompt.promptId, stillPlaying: !!stillPlaying },
          (response = {}) => {
            if (response.ok) {
              resolve(response);
              return;
            }
            reject(new Error(response.error || 'Could not process inactivity prompt'));
          }
        );
      });
      setActiveInactivityPrompt(null);
      setInactivityPromptSecondsRemaining(0);
      await fetchGame();
    } catch (err) {
      setError(err?.message || 'Could not process inactivity prompt');
      if (!stillPlaying) {
        setActiveInactivityPrompt(null);
        setInactivityPromptSecondsRemaining(0);
      }
    } finally {
      setRespondingToInactivityPrompt(false);
    }
  }, [activeInactivityPrompt?.promptId, fetchGame, respondingToInactivityPrompt]);

  useEffect(() => {
    if (!activeInactivityPrompt?.promptId) return;
    if (inactivityPromptSecondsRemaining > 0) return;
    handleInactivityPromptResponse(false);
  }, [activeInactivityPrompt?.promptId, handleInactivityPromptResponse, inactivityPromptSecondsRemaining]);

  const handleJoin = async () => {
    try {
      await joinGame(id);
      fetchGame();
    } catch (err) {
      const message = err.response?.data?.error || 'Could not join';
      // If the user is already seated (or game advanced while joining), push back to table.
      if (message.includes('already') || message.includes('started')) {
        try {
          const { data } = await getGame(id);
          const seatedNow = (data?.players || []).some((p) => p.userId === user?.id);
          if (seatedNow) {
            navigate(`/game/${id}/play`);
            return;
          }
        } catch {
          // Fall through to regular error handling.
        }
      }
      setError(message);
    }
  };

  const handleStart = async () => {
    try {
      await startGame(id);
      navigate(`/game/${id}/play`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start game');
    }
  };

  const handleRequestSeat = async () => {
    try {
      await requestSeatNextRound(id);
      fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not request seat');
    }
  };

  const handlePeekOnGame = () => {
    navigate(`/game/${id}/play`);
  };

  const handleAddNpc = async () => {
    try {
      await addNpcToLobby(id);
      fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add NPC');
    }
  };

  const handleRemoveNpc = async (seatId) => {
    try {
      await removeNpcFromLobby(id, seatId);
      fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove NPC');
    }
  };

  const handleEmergencyStopBots = async () => {
    try {
      setError('');
      setStaleActionMessage('');
      const { data } = await clearNpcs(id);
      setStaleActionMessage(data?.message || 'Emergency stop complete.');
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not stop bots');
    }
  };

  const handleLeaveRoom = async () => {
    try {
      await leaveGame(id);
      fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not leave room');
    }
  };

  const handleSendChat = (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    if (!socketRef.current) return;
    const target = chatTargets.find((entry) => entry.id === selectedChatTargetId);
    if (!target) return;
    if (target.type === CHAT_SCOPE.DM_INBOX) return;
    const outgoing = buildOutgoingMessage(target, user, text);

    if (target.type === CHAT_SCOPE.DM) {
      socketRef.current.emit('send-chat', {
        text: outgoing.body,
        recipientUserId: outgoing.recipientId,
      });
    } else {
      socketRef.current.emit('send-chat', { text: outgoing.body });
    }
    setChatText('');
  };

  const handleTogglePersistence = async () => {
    if (!game) return;
    try {
      setPersistenceSaving(true);
      await updateGamePersistence(id, !game.isPersistent);
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update persistence');
    } finally {
      setPersistenceSaving(false);
    }
  };

  const handleRenameRoom = async (e) => {
    e.preventDefault();
    const trimmed = roomNameInput.trim();
    if (!trimmed) {
      setError('Room name cannot be empty');
      return;
    }
    if (trimmed.length > 60) {
      setError('Room name too long (max 60 chars)');
      return;
    }
    if (!socketRef.current) {
      setError('Socket is not connected yet. Try again in a second.');
      return;
    }

    try {
      setRenamingRoom(true);
      setError('');
      socketRef.current.emit('set-game-name', { name: trimmed });
      await fetchGame();
    } catch {
      setError('Could not rename room');
    } finally {
      setRenamingRoom(false);
    }
  };

  const handleAdvancedSettingChange = (key, value) => {
    setSettingsDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAdvancedSettings = async (e) => {
    e.preventDefault();
    try {
      setSavingAdvancedSettings(true);
      setError('');
      await updateGameSettings(id, settingsDraft);
      await fetchGame();
      setStaleActionMessage('Tournament settings updated.');
      setEditingAdvancedSettings(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update tournament settings');
    } finally {
      setSavingAdvancedSettings(false);
    }
  };

  const handleCollapseInactiveAndRedeal = async () => {
    try {
      setCollapsingInactive(true);
      setError('');
      setStaleActionMessage('');
      const { data } = game?.status === 'swap'
        ? await proposeCollapseInactiveRedeal(id)
        : await collapseInactiveAndRedeal(id);
      setStaleActionMessage(data?.message || 'Inactive users collapsed and redeal attempted.');
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not collapse inactive users');
    } finally {
      setCollapsingInactive(false);
    }
  };

  const handleBumpToWaitlist = async (targetUserId) => {
    try {
      setError('');
      setStaleActionMessage('');
      setBumpingUserId(targetUserId);
      const { data } = await bumpPlayerToWaitlist(id, targetUserId);
      setStaleActionMessage(data?.message
        || (data?.targetDisplayName
          ? `${data.targetDisplayName} removed from room.`
          : 'Player removed from room.'));
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not bump player');
    } finally {
      setBumpingUserId('');
    }
  };

  if (!game) return <div className="lobby-page"><p>Loading...</p></div>;

  const isPrivilegedUser = ['admin', 'moderator', 'stickler'].includes(user?.role);
  const isPrivate = !!game.isPrivate;
  const isPersistent = !!game.isPersistent;
  const isStickler = user?.role === 'stickler';
  const canTogglePersistence = ['admin', 'moderator', 'stickler'].includes(user?.role);
  const isInGame = game.players.some((p) => p.userId === user?.id);
  const hasPendingRequest = (game.pendingJoinRequests || []).some((r) => r.userId === user?.id);
  const isQueuedForNextRound = (game.queuedPlayers || []).some((r) => r.userId === user?.id);
  const isWaitingForSeat = hasPendingRequest || isQueuedForNextRound;
  const canManageWaitingRoom = !!game.canManageWaitingRoom;
  const canRenameRoom = isInGame || isPrivilegedUser;
  const canCollapseInactive = isInGame || canManageWaitingRoom;
  const canManageNpcs = !!game.canManageNpcs;
  const botCount = (game.players || []).filter((p) => p.isBot).length;
  const canJoinLobby = game.status === 'lobby' && (!isPrivate || isPrivilegedUser) && (game.players.length < game.maxPlayers || botCount > 0);
  const canPeekOnGame = game.status !== 'lobby' && (game.players || []).length > 0;
  const humanCount = (game.players || []).filter((p) => !p.isBot).length;
  const roomStatusLabel = formatRoomStatus(game.status);
  const waitingQueue = [
    ...(game.pendingJoinRequests || []),
    ...(game.queuedPlayers || []),
  ].filter((entry, index, arr) => {
    const idKey = entry?.userId?.toString?.() || entry?.displayName || `${index}`;
    return arr.findIndex((candidate) => (candidate?.userId?.toString?.() || candidate?.displayName || '') === idKey) === index;
  });
  const lobbyLeaderboard = [...(game.sessionStats || [])]
    .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
  const chatParticipantsMap = new Map();
  const upsertChatParticipant = ({ userId, displayName, isOnline = true }) => {
    const uid = `${userId || ''}`;
    if (!uid || uid === `${user?.id || ''}`) return;
    if (!chatParticipantsMap.has(uid)) {
      chatParticipantsMap.set(uid, {
        userId: uid,
        displayName: displayName || 'Player',
        isOnline: !!isOnline,
      });
      return;
    }
    const existing = chatParticipantsMap.get(uid);
    chatParticipantsMap.set(uid, {
      ...existing,
      displayName: existing.displayName || displayName || 'Player',
      isOnline: existing.isOnline || !!isOnline,
    });
  };

  (game.chatParticipants || []).forEach((entry) => {
    upsertChatParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: entry?.isOnline });
  });
  (game.players || []).forEach((p) => {
    if (p?.isBot) return;
    upsertChatParticipant({ userId: p?.userId, displayName: p?.displayName, isOnline: p?.isActiveInRoom });
  });
  (game.pendingJoinRequests || []).forEach((entry) => {
    upsertChatParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });
  (game.queuedPlayers || []).forEach((entry) => {
    upsertChatParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });

  const loungeChatMessages = game.loungeChatMessages || [];
  const gameChatMessages = game.gameChatMessages || [];
  [...loungeChatMessages, ...gameChatMessages].forEach((msg) => {
    upsertChatParticipant({ userId: msg?.userId, displayName: msg?.displayName, isOnline: true });
    upsertChatParticipant({ userId: msg?.recipientUserId, displayName: msg?.recipientDisplayName, isOnline: true });
  });

  const chatParticipants = Array.from(chatParticipantsMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  const dmTargets = chatParticipants.map((entry) => buildDmTarget(entry));
  const chatTargets = [buildRoomLoungeTarget({ roomId: id, roomName: game?.gameName, roomCode: game?.roomCode }), buildDmInboxTarget(), ...dmTargets];
  const selectedChatTarget = chatTargets.find((entry) => entry.id === selectedChatTargetId)
    || chatTargets.find((entry) => entry.id === `room-game:${id}`)
    || chatTargets[0];

  const normalizedMessages = dedupeAndSortNormalizedMessages([
    ...loungeChatMessages.map((msg) => toNormalizedMessage({ message: msg, fallbackScope: CHAT_SCOPE.ROOM_GAME, roomId: id })),
    ...gameChatMessages.map((msg) => toNormalizedMessage({ message: msg, fallbackScope: CHAT_SCOPE.ROOM_GAME, roomId: id })),
  ]).filter(Boolean);

  const selectedTargetHistoryNormalized = dedupeAndSortNormalizedMessages(
    (selectedTargetHistoryMessages || []).map((msg) => toNormalizedMessage({
      message: msg,
      fallbackScope: selectedChatTarget?.type || CHAT_SCOPE.ROOM_GAME,
      roomId: selectedChatTarget?.roomId || id,
    }))
  ).filter(Boolean);

  const visibleMessages = dedupeAndSortNormalizedMessages([
    ...normalizedMessages,
    ...selectedTargetHistoryNormalized,
  ]).filter((msg) => messageMatchesTarget(msg, selectedChatTarget, getUserId(user)));

  const unread = computeUnreadByTarget({
    targets: chatTargets,
    messages: dedupeAndSortNormalizedMessages([
      ...normalizedMessages,
      ...selectedTargetHistoryNormalized,
    ]),
    currentUserId: getUserId(user),
    lastSeenAtByTargetId,
  });
  const unreadByTargetId = {
    ...unread.unreadByTargetId,
    [selectedChatTarget?.id || '']: 0,
  };
  const totalUnread = Object.values(unreadByTargetId).reduce((sum, value) => sum + Number(value || 0), 0);
  const unreadNotices = unread.notices.filter((entry) => entry.targetId !== selectedChatTarget?.id);

  const selectedTargetParticipants = selectedChatTarget?.type === CHAT_SCOPE.DM
    ? chatParticipants.filter((entry) => `${entry.userId}` === `${selectedChatTarget.recipientId || ''}`)
    : chatParticipants;

  const currentUserId = getUserId(user);
  const selectedVoicePipeId = toVoicePipeIdFromTarget(selectedChatTarget, currentUserId);
  const roomVoiceLabel = chatTargets.find((entry) => entry.type === CHAT_SCOPE.ROOM_GAME)?.label || 'Room Pipe';
  const pipeLabelById = {
    room: roomVoiceLabel,
  };
  chatTargets
    .filter((entry) => entry.type === CHAT_SCOPE.DM && entry.recipientId)
    .forEach((entry) => {
      const pipeId = toDmVoicePipeId(currentUserId, entry.recipientId);
      pipeLabelById[pipeId] = `Private Pipe ${toShortPipeCode(pipeId)}`;
    });
  const selectedVoicePipeLabel = pipeLabelById[selectedVoicePipeId] || 'Room Pipe';
  const activeVoicePipeCode = toShortPipeCode(activePipeId);
  const activeVoicePipeLabelBase = pipeLabelById[activeVoiceTarget]
    || (activeVoiceTarget?.startsWith('dm:') ? `Private Pipe ${toShortPipeCode(activeVoiceTarget)}` : roomVoiceLabel);
  const activeVoicePipeLabel = activeVoicePipeCode
    ? `${activeVoicePipeLabelBase} [${activeVoicePipeCode}]`
    : activeVoicePipeLabelBase;

  const markTargetSeen = (targetId) => {
    if (!targetId) return;
    setLastSeenAtByTargetId((prev) => ({
      ...prev,
      [targetId]: new Date().toISOString(),
    }));
  };

  const handleSelectTarget = (targetId) => {
    setSelectedChatTargetId(targetId);
    markTargetSeen(targetId);
  };

  const removeMessageById = (entry, messageId) => `${entry?.messageId || entry?._id || entry?.id || ''}` !== `${messageId || ''}`;

  const handleDeleteChatMessage = async (msg) => {
    const messageId = `${msg?.id || ''}`;
    if (!messageId) return;
    try {
      await deleteChatMessage(messageId, {
        scope: msg?.scope || selectedChatTarget?.type || '',
        roomId: id,
      });

      setSelectedTargetHistoryMessages((prev) => prev.filter((entry) => removeMessageById(entry, messageId)));
      setGame((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          loungeChatMessages: (prev.loungeChatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
          gameChatMessages: (prev.gameChatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
          chatMessages: (prev.chatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
        };
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete chat message');
    }
  };

  return (
    <div className="lobby-page">
      <ThinHeader
        homeTo="/"
        onToggleLeaderboard={() => setShowHeaderLeaderboard((prev) => !prev)}
        leaderboardOpen={showHeaderLeaderboard}
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      {activeWaitlistOffer && !isInGame && (
        <div className="socket-error-overlay" role="dialog" aria-modal="true" aria-labelledby="waitlist-offer-title">
          <div className="socket-error-modal">
            <h3 id="waitlist-offer-title">Join This Game?</h3>
            <p className="socket-error-body">You are currently in the waiting room. Join now or remain waitlisted.</p>
            <p className="socket-error-help">{formatJoinWindowReason(game?.roundJoinOfferWindow?.reason)}: {offerSecondsRemaining}s</p>
            <div className="socket-error-actions">
              <button
                className="btn-cancel"
                onClick={() => handleWaitlistOfferResponse(false)}
                disabled={respondingToOffer}
              >
                Stay Waitlisted
              </button>
              <button
                className="btn-play"
                onClick={() => handleWaitlistOfferResponse(true)}
                disabled={respondingToOffer}
              >
                {respondingToOffer ? 'Saving...' : 'Join This Game'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeInactivityPrompt && (
        <div className="socket-error-overlay" role="dialog" aria-modal="true" aria-labelledby="lobby-inactivity-title">
          <div className="socket-error-modal">
            <h3 id="lobby-inactivity-title">Are You Still Playing?</h3>
            <p className="socket-error-body">Confirm now to keep your seat. If the timer expires, NPC autopilot will take over and reserve your return spot.</p>
            <p className="socket-error-help">{inactivityPromptSecondsRemaining}s remaining.</p>
            <div className="socket-error-actions">
              <button
                className="btn-cancel"
                onClick={() => handleInactivityPromptResponse(false)}
                disabled={respondingToInactivityPrompt}
              >
                Let NPC Take Over
              </button>
              <button
                className="btn-play"
                onClick={() => handleInactivityPromptResponse(true)}
                disabled={respondingToInactivityPrompt}
              >
                {respondingToInactivityPrompt ? 'Saving...' : "Yes, I'm Here"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHeaderLeaderboard && (
        <div className="header-leaderboard-panel">
          <h3>Room Leaderboard</h3>
          <h4 className="lounge-player-heading">
            {leaderboardMode === 'all-time' ? 'All-Time Players (Points)' : `${leaderboardMode === 'monthly' ? 'Monthly' : 'Weekly'} Tournament Players`}
          </h4>
          <div className="leaderboard-actions-row">
            <button
              type="button"
              className="thin-header-btn"
              onClick={() => {
                setLeaderboardMode('all-time');
                setShowAllTimeAll(false);
              }}
            >
              All-Time
            </button>
            <button
              type="button"
              className="thin-header-btn"
              onClick={() => {
                setLeaderboardMode('monthly');
                setShowAllTimeAll(true);
              }}
            >
              Monthly Tournament
            </button>
            <button
              type="button"
              className="thin-header-btn"
              onClick={() => {
                setLeaderboardMode('weekly');
                setShowAllTimeAll(true);
              }}
            >
              Weekly Tournament
            </button>
            <button
              type="button"
              className="thin-header-btn"
              disabled={leaderboardMode !== 'all-time'}
              onClick={() => setShowAllTimeAll((prev) => !prev)}
            >
              {showAllTimeAll ? 'Show Top 10' : 'Expand To All'}
            </button>
          </div>
          {leaderboardMode !== 'all-time' && periodMeta && (
            <p className="admin-note" style={{ marginBottom: '0.55rem' }}>
              {periodMeta.periodLabel || (leaderboardMode === 'monthly' ? 'Monthly period' : 'Weekly period')}
              {' '}• {periodMeta.tournamentName || 'Tournament'}
              {' '}• {periodMeta.participantsTotal || 0} participants
            </p>
          )}
          {allTimeLoading ? (
            <p className="header-leaderboard-empty">Loading all-time leaderboard...</p>
          ) : (leaderboardMode === 'all-time' ? allTimeLeaderboard : periodLeaderboard).length > 0 ? (
            <ul>
              {(leaderboardMode === 'all-time' ? allTimeLeaderboard : periodLeaderboard).map((entry) => {
                const activity = formatLeaderboardActivity(entry);
                return (
                  <li key={`all-time-${entry.userId}`}>
                    <span className="leader-rank">#{entry.rank}</span>
                    <span className="leader-name">{entry.displayName}</span>
                    <span className="leader-metrics">{entry.totalPoints ?? 0} pts</span>
                    <span
                      className={`leader-activity-badge ${activity.cssClass}`}
                      title={activity.title}
                    >
                      {activity.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="header-leaderboard-empty">
              {leaderboardMode === 'all-time' ? 'No all-time player stats yet.' : 'No tournament player stats for this period yet.'}
            </p>
          )}

          <h4 className="lounge-player-heading">Players In Lounge</h4>
          <ul>
            {game.players.map((p, idx) => (
              <li key={`lounge-player-${p.userId || p.displayName}-${idx}`}>
                <span className="leader-rank">•</span>
                <span className="leader-name">{p.displayName}</span>
                <span className="leader-metrics">{p.userId === game.hostId ? 'Host' : (p.isBot ? 'NPC' : 'Player')}</span>
                {!p.isBot && (
                  <span className={`leader-activity-badge ${p.isActiveInRoom ? 'online' : 'offline'}`}>
                    {p.isActiveInRoom ? 'In room' : 'Inactive in room'}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {lobbyLeaderboard.length > 0 ? (
            <>
              <h4 className="lounge-player-heading">Room Stats (Player + Points)</h4>
              <ul>
                {lobbyLeaderboard.map((entry, idx) => (
                  <li key={`${entry.userId ?? 'bot'}-${entry.displayName}`}>
                    <span className="leader-rank">#{idx + 1}</span>
                    <span className="leader-name">{entry.displayName}</span>
                    <span className="leader-metrics">{entry.totalPoints ?? 0} pts • {entry.roundsWon ?? 0} wins • {entry.courtesyShuffles ?? 0} courtesy shuffles</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="header-leaderboard-empty">No rounds completed yet in this room.</p>
          )}
        </div>
      )}

      <h1>{game.gameName || `Room ${game.roomCode}`}</h1>
      <div className="section-header-row" style={{ marginBottom: '0.55rem' }}>
        <h2 style={{ marginBottom: 0 }}>Room Settings</h2>
        <button type="button" className="section-toggle-btn" onClick={() => setRoomSettingsOpen((prev) => !prev)}>
          {roomSettingsOpen ? 'Collapse' : 'Expand'}
        </button>
      </div>
      {roomSettingsOpen && (
        <>
          <div className="room-meta-block">
            <p>Room code: {game.roomCode}</p>
            <p>Status: {roomStatusLabel}{isPrivate ? ' • Private Room' : ''}</p>
            <p>Persistence: {isPersistent ? 'ON (protected from auto-close)' : 'OFF (auto-close allowed)'}</p>
            {game.tournament?.enabled && (
              <p>
                Tournament: {game.tournament?.label || 'Unnamed'} • {game.tournament?.recurrence || 'none'} • {game.tournament?.mode === 'long-play' ? 'Long-Play' : 'Live'}
                {game.tournament?.inactivityTimerEnabled ? ` • Timer ${game.tournament?.inactivityTimeoutMinutes || 30}m` : ' • Timer Off'}
              </p>
            )}
          </div>
          <form onSubmit={handleRenameRoom} className="room-rename-row">
            <input
              type="text"
              className="room-rename-input"
              value={roomNameInput}
              maxLength={60}
              onChange={(e) => setRoomNameInput(e.target.value)}
              disabled={!canRenameRoom || renamingRoom}
              placeholder="Rename room"
            />
            <button type="submit" className="btn-cancel room-rename-btn" disabled={!canRenameRoom || renamingRoom}>
              {renamingRoom ? 'Saving...' : 'Rename Room'}
            </button>
            {!canRenameRoom && <small className="room-rename-note">Join this room to rename it.</small>}
          </form>
          {canTogglePersistence && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleTogglePersistence} className="thin-header-btn" disabled={persistenceSaving}>
                {persistenceSaving
                  ? 'Saving...'
                  : (isPersistent ? 'Turn Persistence OFF (allow auto-close)' : 'Turn Persistence ON (prevent auto-close)')}
              </button>
              <button
                type="button"
                className="thin-header-btn"
                onClick={() => setEditingAdvancedSettings((prev) => !prev)}
              >
                {editingAdvancedSettings ? 'Hide Tournament Settings' : 'Edit Tournament Settings'}
              </button>
            </div>
          )}
          {canTogglePersistence && editingAdvancedSettings && (
            <form onSubmit={handleSaveAdvancedSettings} className="auth-form" style={{ marginTop: '0.85rem' }}>
              <label className="create-field-label">
                <input
                  type="checkbox"
                  checked={settingsDraft.tournamentEnabled}
                  onChange={(e) => handleAdvancedSettingChange('tournamentEnabled', e.target.checked)}
                />
                Tournament room enabled
              </label>
              {settingsDraft.tournamentEnabled && (
                <>
                  <label className="create-field-label create-field-label--stacked">
                    Tournament recurrence:
                    <select
                      value={settingsDraft.tournamentRecurrence}
                      onChange={(e) => handleAdvancedSettingChange('tournamentRecurrence', e.target.value)}
                    >
                      <option value="none">One-Time</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </label>
                  <label className="create-field-label create-field-label--stacked">
                    Tournament label:
                    <input
                      type="text"
                      maxLength={80}
                      value={settingsDraft.tournamentLabel}
                      onChange={(e) => handleAdvancedSettingChange('tournamentLabel', e.target.value)}
                      placeholder="Monthly Mayhem"
                    />
                  </label>
                  <label className="create-field-label create-field-label--stacked">
                    Tournament mode:
                    <select
                      value={settingsDraft.tournamentMode}
                      onChange={(e) => handleAdvancedSettingChange('tournamentMode', e.target.value)}
                    >
                      <option value="live">Live Tournament</option>
                      <option value="long-play">Long-Play Tournament</option>
                    </select>
                  </label>
                  <label className="create-field-label">
                    <input
                      type="checkbox"
                      checked={settingsDraft.tournamentInactivityTimerEnabled}
                      disabled={settingsDraft.tournamentMode !== 'live'}
                      onChange={(e) => handleAdvancedSettingChange('tournamentInactivityTimerEnabled', e.target.checked)}
                    />
                    Inactivity timer enabled
                  </label>
                  {settingsDraft.tournamentMode === 'live' && settingsDraft.tournamentInactivityTimerEnabled && (
                    <label className="create-field-label create-field-label--stacked">
                      Inactivity timeout:
                      <select
                        value={settingsDraft.tournamentInactivityTimeoutMinutes}
                        onChange={(e) => handleAdvancedSettingChange('tournamentInactivityTimeoutMinutes', Number(e.target.value))}
                      >
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={20}>20 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                      </select>
                    </label>
                  )}
                </>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="submit" className="btn-play" disabled={savingAdvancedSettings}>
                  {savingAdvancedSettings ? 'Saving...' : 'Save Tournament Settings'}
                </button>
                <button type="button" className="btn-cancel" onClick={() => setEditingAdvancedSettings(false)} disabled={savingAdvancedSettings}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
      {forceWaitingRoomView && (game.status === 'swap' || game.status === 'playing') && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <button className="btn-play" onClick={() => navigate(`/game/${id}/play`)}>
            Return To Game Table
          </button>
          {isInGame && <small>You are still seated and counted for this round.</small>}
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {staleActionMessage && <p className="profile-success">{staleActionMessage}</p>}

      <h2>Players ({game.players.length}/{game.maxPlayers})</h2>
      <ul>
        {game.players.map((p, idx) => (
          <li key={`${p.userId || p.displayName}-${idx}`}>
            <span className={`game-activity-dot ${p.isActiveInRoom ? 'active' : ''}`} style={{ marginRight: '0.45rem' }} aria-hidden="true" />
            {p.displayName}
            {p.isBot ? ' 🤖' : ''}
            {p.userId === game.hostId ? ' 👑' : ''}
            {!p.isBot && !p.isActiveInRoom ? ' (inactive)' : ''}
            {canManageNpcs && game.status !== 'playing' && p.isBot && (
              <button
                type="button"
                className="btn-cancel"
                style={{ marginLeft: '0.6rem' }}
                onClick={() => handleRemoveNpc(p.seatId || p.displayName)}
              >
                Remove NPC
              </button>
            )}
            {!p.isBot && p.userId !== user?.id && game.canBumpPlayers && game.status !== 'playing' && (!p.isActiveInRoom || isStickler) && (
              <button
                type="button"
                className="btn-cancel"
                style={{ marginLeft: '0.6rem' }}
                onClick={() => handleBumpToWaitlist(p.userId)}
                disabled={bumpingUserId === p.userId}
              >
                {bumpingUserId === p.userId ? 'Removing...' : 'Toss Out'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {canManageNpcs && game.status === 'lobby' && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {game.players.length < game.maxPlayers && (
            <button onClick={handleAddNpc} className="btn-cancel">Add NPC To Empty Tile</button>
          )}
          {botCount > 0 && (
            <button onClick={handleEmergencyStopBots} className="btn-cancel">Remove All NPCs</button>
          )}
        </div>
      )}

      {!isInGame && canJoinLobby && (
        <button onClick={handleJoin} className="btn-play room-action-btn room-action-join">Join Game</button>
      )}

      {!isInGame && game.status === 'lobby' && isPrivate && !isPrivilegedUser && (
        <p>This room is private. Join and seat requests are disabled for non-members.</p>
      )}

      {!isInGame && game.status !== 'lobby' && !isPrivate && !isWaitingForSeat && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {canPeekOnGame && <button onClick={handlePeekOnGame} className="btn-cancel">Peek On Game (Watch Only)</button>}
          <button onClick={handleRequestSeat} className="btn-play room-action-btn room-action-watch">Reserve Spot</button>
        </div>
      )}

      {!isInGame && game.status !== 'lobby' && isPrivate && (
        <p>This room is private. Seat requests are disabled.</p>
      )}

      {!isInGame && isWaitingForSeat && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <p>{canPeekOnGame ? 'You are queued, can watch now, and will be seated when a spot opens.' : 'You are queued and will be seated when a spot opens.'}</p>
          <button className="btn-cancel" onClick={handleLeaveRoom}>Unreserve Spot</button>
          {canPeekOnGame && <button className="btn-cancel" onClick={handlePeekOnGame}>Peek On Game</button>}
        </div>
      )}

      {isInGame && (
        <button onClick={handleLeaveRoom} className="btn-cancel">
          {game.status === 'swap' || game.status === 'playing'
            ? 'Leave Game (Replace Self With NPC)'
            : 'Leave Table (Room Stays Open)'}
        </button>
      )}

      {(waitingQueue.length > 0 || (canCollapseInactive && game.status !== 'lobby')) && (
        <section className="waiting-room-panel">
          <h2>Waiting Room</h2>
          {canManageNpcs && botCount > 0 && game.status !== 'playing' && (
            <div style={{ marginBottom: '0.8rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn-cancel" onClick={handleEmergencyStopBots}>
                Remove All NPCs
              </button>
              <small>Clears all NPCs and returns the table to lobby if needed.</small>
            </div>
          )}
          {canCollapseInactive && game.status !== 'lobby' && (
            <div style={{ marginBottom: '0.8rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn-cancel" onClick={handleCollapseInactiveAndRedeal} disabled={collapsingInactive}>
                {collapsingInactive ? 'Collapsing...' : 'Collapse Non-Active Users + Redeal'}
              </button>
              <small>Use this when a game is stale because missing players are blocking progress.</small>
            </div>
          )}

          <h3>Queued For Next Round</h3>
          {waitingQueue.length ? (
            <ul>
              {waitingQueue.map((reqEntry, idx) => (
                <li key={`${reqEntry.userId || reqEntry.displayName}-${idx}`}>
                  {reqEntry.displayName}
                  {reqEntry.reservationType === 'inactivity-autopilot' ? ' (Autopilot Reserved)' : ''}
                </li>
              ))}
            </ul>
          ) : <p>No players queued yet.</p>}
        </section>
      )}

      <UniversalChatDock
        collapsed={chatCollapsed}
        onToggleCollapsed={() => setChatCollapsed((prev) => !prev)}
        targets={chatTargets}
        selectedTargetId={selectedChatTarget?.id || ''}
        onSelectTarget={handleSelectTarget}
        participants={selectedTargetParticipants}
        messages={visibleMessages}
        currentUserId={getUserId(user)}
        inputValue={chatText}
        onInputChange={setChatText}
        onSend={handleSendChat}
        placeholder={getPlaceholderForTarget(selectedChatTarget)}
        unreadCount={totalUnread}
        unreadByTargetId={unreadByTargetId}
        unreadNotices={unreadNotices}
        onNoticeClick={handleSelectTarget}
        onReplyToSender={(msg) => {
          if (!msg?.senderId) return;
          handleSelectTarget(`dm:${msg.senderId}`);
        }}
        canDeleteMessages={canModerateChat}
        onDeleteMessage={handleDeleteChatMessage}
        emptyState="No messages yet for this target."
        voiceControls={{
          enabled: true,
          isJoined: voiceJoined,
          isMuted: voiceMuted,
          remoteCount: remotePeers.length,
          activePipeLabel: activeVoicePipeLabel,
          selectedPipeLabel: selectedVoicePipeLabel,
          error: voiceError,
          onJoin: joinVoice,
          onJoinSelectedPipe: async () => {
            await joinVoiceTarget(selectedVoicePipeId);
          },
          onLeave: leaveVoice,
          onToggleMute: toggleVoiceMute,
          incomingCall,
          onAcceptIncomingCall: acceptIncomingCall,
          onDeclineIncomingCall: () => declineIncomingCall('declined'),
          onStartPrivateCall: async (target) => {
            const recipientId = `${target?.recipientId || ''}`;
            if (!recipientId) return;
            await callPeer(recipientId, target?.recipientName || 'Player');
          },
        }}
      />

      <div style={{ display: 'none' }}>
        {remotePeers.map((peer) => (
          <VoiceRemoteAudio key={`lounge-voice-audio-${peer.userId}`} stream={peer.stream} />
        ))}
      </div>

      {(isInGame || isPrivilegedUser) && humanCount >= 1 && game.players.length >= 2 && (
        <button onClick={handleStart} className="btn-start">
          Deal Cards &amp; Start Swap Phase
        </button>
      )}

      {(isInGame || isPrivilegedUser) && (game.players.length < 2 || humanCount < 1) && (
        <p>
          {humanCount < 1
            ? 'No human players seated yet. Add yourself or another player, then start the game.'
            : 'Waiting for at least 2 total players to join…'}
        </p>
      )}
    </div>
  );
};

export default LobbyPage;
